import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { FamilyProvider } from "@/contexts/FamilyContext";

import ConnectionStatus from "@/components/ConnectionStatus";
import { TESTING_MODE } from "@/lib/testing-mode";
import Index from "./pages/Index";

const Auth = lazy(() => import("./pages/Auth"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const KidView = lazy(() => import("./pages/KidView"));
const FamilyDashboard = lazy(() => import("./pages/FamilyDashboard"));
const PinSwitch = lazy(() => import("./pages/PinSwitch"));
const KidJoin = lazy(() => import("./pages/KidJoin"));
const KidLogin = lazy(() => import("./pages/KidLogin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const BillingSmokeTest = lazy(() => import("./pages/BillingSmokeTest"));
const AdminBetaTesters = lazy(() => import("./pages/AdminBetaTesters"));
const AdminStripeReadiness = lazy(() => import("./pages/AdminStripeReadiness"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const WallLogin = lazy(() => import("./pages/WallLogin"));
const WallUnpair = lazy(() => import("./pages/WallUnpair"));
import WallDeviceGuard from "@/components/WallDeviceGuard";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,           // 30 seconds
      gcTime: 5 * 60 * 1000,          // 5 minutes cache
      refetchOnWindowFocus: false,    // Don't refetch on tab focus
      refetchOnMount: false,          // Use cached data on remount
      retry: 1,                       // Don't retry 3 times on failure
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const DirectionWrapper = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const dir = i18n.language?.startsWith('ar') ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language || 'en';
    document.body.dir = dir;
  }, [dir, i18n.language]);

  return (
    <div dir={dir} className="min-h-screen">
      {TESTING_MODE && (
        <div className="sticky top-0 z-[100] bg-amber-500 text-amber-950 text-center py-1.5 px-4 text-xs sm:text-sm font-bold shadow-md">
          ⚠️ Testing Mode — Login Disabled (for Grok review only)
        </div>
      )}
      {children}
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <DirectionWrapper>
          <FamilyProvider>
            <ConnectionStatus />
            <WallDeviceGuard />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/parent" element={<ParentDashboard />} />
                <Route path="/kid/:id" element={<KidView />} />
                <Route path="/dashboard" element={<FamilyDashboard />} />
                <Route path="/join/:code" element={<KidJoin />} />
                <Route path="/switch" element={<PinSwitch />} />
                <Route path="/kid-login" element={<KidLogin />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin-portal" element={<AdminPortal />} />
                <Route path="/admin/billing-smoke" element={<BillingSmokeTest />} />
                <Route path="/admin/beta" element={<AdminBetaTesters />} />
                <Route path="/admin/stripe-readiness" element={<AdminStripeReadiness />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/wall-login" element={<WallLogin />} />
                <Route path="/wall/unpair" element={<WallUnpair />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </FamilyProvider>
        </DirectionWrapper>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
